import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "auth";
import { useDispatch, useSelector } from "react-redux";
import { setCardInfo } from "store/userSlice";
import { networkTypeSelector } from "store/userSliceHelpers";
import { getCardInfo } from "utils/AxiosUtil";
import { getCardType, getStatusType } from "utils/card";

export interface MappedCardInfo {
  cardNumber: string;
  cardAddress?: string;
  cardAlias?: string;
  txc?: number;
  cardStatus?: string;
  cardType: string;
  lastUpdated: number;
}

export function useCardInfo(cardNumber: string | undefined) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const networkType = useSelector(networkTypeSelector);
  const query = useQuery<MappedCardInfo>({
    queryKey: ["cardInfo", cardNumber],
    queryFn: async () => {
      const cardinfo = await getCardInfo(cardNumber!, networkType);
      return {
        cardNumber: cardNumber!,
        cardAddress: cardinfo?.cardAddress,
        cardAlias: cardinfo?.aliesCardAddress,
        txc: cardinfo?.counter,
        cardStatus: getStatusType(cardinfo?.cardStatus),
        cardType: getCardType(cardNumber!),
        lastUpdated: Date.now() / 1000,
      };
    },
    enabled: !!user && !!cardNumber,
  });

  // Mirrors the RESOLVED value, not the fetch. Dispatching inside queryFn left the redux copy on
  // whichever card last missed the cache — and staleTime is Infinity, so switching back to an
  // already-loaded card is a pure hit. UserPage reads the alias, status and type from redux while
  // the balance comes from the query, so the panel showed one card's number and balance beside
  // another card's address, QR and explorer link.
  useEffect(() => {
    if (query.data) dispatch(setCardInfo(query.data));
  }, [query.data, dispatch]);

  return query;
}
